import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../store/store";
import useAlerts from "../useAlerts";
import useLoading from "../useLoading";
import useApiErrorHandler from "../useApiErrorHandler";

import ModuleService from "../../services/module/ModuleService";
import {
  setModules,
  setModuleDetail,
  clearModuleDetail,
  resetModuleState,
  updateModuleNameSuccess,
  updateModuleCategorySuccess,
  updateModuleOrderSuccess,
  updateModuleRolesSuccess,
  updateModuleIconSuccess,
  updateModuleRouteSuccess,
  toggleModuleStatusSuccess,
  updateModuleHasChildrenSuccess,
  updateModuleField,
} from "../../store/slices/module/moduleSlice";
import { setLoading } from "../../store/slices/routes/routesSlice";

export const useModule = () => {
  const dispatch: AppDispatch = useDispatch();
  const { show: showAlert } = useAlerts();
  const { showLoadingIndicator, hideLoadingIndicator } = useLoading();
  const { handle } = useApiErrorHandler();

  const { modules, moduleDetail } = useSelector(
    (state: RootState) => state.module
  );

  // ===================================================
  // 🌐 CONSULTAS
  // ===================================================
  const fetchModules = async () => {
    try {
      showLoadingIndicator();
      dispatch(setLoading(true));
      const response = await ModuleService.getModules();
      const { statusCode, type, title, message, data } = response;

      if (statusCode === 200 && data) {
        dispatch(setModules(data));
      }
      showAlert(type, title, message);
    } catch (error: any) {
      handle(error);
    } finally {
      hideLoadingIndicator();
      dispatch(setLoading(false));
    }
  };

  const fetchModuleDetail = async (moduleId: number) => {
    try {
      showLoadingIndicator();
      dispatch(setLoading(true));
      const response = await ModuleService.getModuleDetail(moduleId);
      // console.log(response)
      const { statusCode, type, title, message, data } = response;

      if (statusCode === 200 && data) {
        dispatch(setModuleDetail(data));
      }
      showAlert(type, title, message);
    } catch (error: any) {
      handle(error);
    } finally {
      hideLoadingIndicator();
      dispatch(setLoading(false));
    }
  };

  const clearDetail = () => dispatch(clearModuleDetail());
  const reset = () => dispatch(resetModuleState());

  // ===================================================
  // ✏️ ACTUALIZACIONES GRANULARES
  // ===================================================
  const updateModuleName = async (payload: { moduleId: number; newName: string }) => {
    try {
      showLoadingIndicator();
      const response = await ModuleService.updateModuleName(payload);
      const { statusCode, type, title, message, data } = response;

      if (statusCode === 200 && data) {
        dispatch(updateModuleNameSuccess({ newName: data }));
      }
      showAlert(type, title, message);
    } catch (error: any) {
      handle(error);
    } finally {
      hideLoadingIndicator();
    }
  };

  const updateModuleCategory = async (payload: { moduleId: number; categoryId: number }) => {
    try {
      showLoadingIndicator();
      const response = await ModuleService.updateModuleCategory(payload);
      const { statusCode, type, title, message, data } = response;

      if (statusCode === 200 && data) {
        dispatch(updateModuleCategorySuccess({ categoryId: data }));
      }
      showAlert(type, title, message);
    } catch (error: any) {
      handle(error);
    } finally {
      hideLoadingIndicator();
    }
  };

  const updateModuleOrder = async (payload: { moduleId: number; newOrder: number }) => {
    try {
      showLoadingIndicator();
      const response = await ModuleService.updateModuleOrder(payload);
      const { statusCode, type, title, message, data } = response;

      if (statusCode === 200 && data) {
        dispatch(updateModuleOrderSuccess({ newOrder: data }));
      }
      showAlert(type, title, message);
    } catch (error: any) {
      handle(error);
    } finally {
      hideLoadingIndicator();
    }
  };

  const updateModuleRoles = async (payload: { moduleId: number; roleIds: number[] }) => {
    try {
      showLoadingIndicator();
      const response = await ModuleService.updateModuleRoles(payload);
      const { statusCode, type, title, message, data } = response;

      if (statusCode === 200 && data) {
        dispatch(updateModuleRolesSuccess({ roles: data }));
      }
      showAlert(type, title, message);
    } catch (error: any) {
      handle(error);
    } finally {
      hideLoadingIndicator();
    }
  };

  const updateModuleIcon = async (payload: { moduleId: number; newIcon: string }) => {
    try {
      showLoadingIndicator();
      const response = await ModuleService.updateModuleIcon(payload);
      const { statusCode, type, title, message, data } = response;

      if (statusCode === 200 && data) {
        dispatch(updateModuleIconSuccess({ newIcon: data }));
      }
      showAlert(type, title, message);
    } catch (error: any) {
      handle(error);
    } finally {
      hideLoadingIndicator();
    }
  };

  const updateModuleRoute = async (payload: { moduleId: number; newRoute: string }) => {
    try {
      showLoadingIndicator();
      const response = await ModuleService.updateModuleRoute(payload);
      const { statusCode, type, title, message, data } = response;

      if (statusCode === 200 && data) {
        dispatch(updateModuleRouteSuccess({ newRoute: data }));
      }
      showAlert(type, title, message);
    } catch (error: any) {
      handle(error);
    } finally {
      hideLoadingIndicator();
    }
  };

  const toggleModuleStatus = async (payload: { moduleId: number; disabled: boolean }) => {
    try {
      showLoadingIndicator();
      const response = await ModuleService.toggleModuleStatus(payload);
      const { statusCode, type, title, message, data } = response;

      if (statusCode === 200) {
        dispatch(toggleModuleStatusSuccess({ disabled: data }));
      }
      showAlert(type, title, message);
    } catch (error: any) {
      handle(error);
    } finally {
      hideLoadingIndicator();
    }
  };

  const updateModuleHasChildren = async (payload: { moduleId: number; hasChildren: boolean }) => {
    try {
      showLoadingIndicator();
      const response = await ModuleService.updateModuleHasChildren(payload);
      const { statusCode, type, title, message, data } = response;

      if (statusCode === 200) {
        dispatch(updateModuleHasChildrenSuccess({ hasChildren: data }));
      }
      showAlert(type, title, message);
    } catch (error: any) {
      handle(error);
    } finally {
      hideLoadingIndicator();
    }
  };

  // ===================================================
  // ✏️ ACTUALIZACIÓN LOCAL (sin backend)
  // ===================================================
  const updateFieldLocally = (field: keyof typeof moduleDetail, value: any) => {
    dispatch(updateModuleField({ field, value }));
  };

  return {
    modules,
    moduleDetail,
    fetchModules,
    fetchModuleDetail,
    clearDetail,
    reset,
    updateModuleName,
    updateModuleCategory,
    updateModuleOrder,
    updateModuleRoles,
    updateModuleIcon,
    updateModuleRoute,
    toggleModuleStatus,
    updateModuleHasChildren,
    updateFieldLocally,
  };
};
